/**
 * Lazy Laravel Echo singleton (Reverb).
 *
 * Echo and pusher-js are loaded only when `VITE_REVERB_APP_KEY` is set, so a
 * deployment without a websocket server never downloads them and the app runs
 * on polling alone. Private and presence channels are authorised against
 * `${VITE_APP_URL}/broadcasting/auth` (no `/api` prefix) with the Sanctum
 * cookie and the XSRF header, through the shared axios instance.
 */
import type Echo from 'laravel-echo';
import { api } from '../services/api';

export type RealtimeEcho = Echo<'reverb'>;

const APP_BASE_URL = import.meta.env.VITE_APP_URL ?? '';

let instance: Promise<RealtimeEcho | null> | null = null;
let testOverride: RealtimeEcho | null | undefined;

/** True when the deployment has a websocket server configured. */
export function isRealtimeConfigured(): boolean {
  return Boolean(import.meta.env.VITE_REVERB_APP_KEY?.trim());
}

interface ReverbEnv {
  key: string;
  host: string;
  port: number;
  forceTLS: boolean;
}

function readReverbEnv(): ReverbEnv | null {
  const key = import.meta.env.VITE_REVERB_APP_KEY?.trim();
  if (!key) return null;
  const scheme = (
    import.meta.env.VITE_REVERB_SCHEME?.trim() ||
    (typeof window !== 'undefined' && window.location.protocol === 'https:'
      ? 'https'
      : 'http')
  ).toLowerCase();
  const forceTLS = scheme === 'https';
  const host =
    import.meta.env.VITE_REVERB_HOST?.trim() ||
    (typeof window !== 'undefined' ? window.location.hostname : 'localhost');
  const parsedPort = Number(import.meta.env.VITE_REVERB_PORT);
  const port =
    Number.isInteger(parsedPort) && parsedPort > 0
      ? parsedPort
      : forceTLS
        ? 443
        : 80;
  return { key, host, port, forceTLS };
}

async function createEcho(): Promise<RealtimeEcho | null> {
  const env = readReverbEnv();
  if (!env) return null;
  try {
    const [{ default: EchoClient }, { default: Pusher }] = await Promise.all([
      import('laravel-echo'),
      import('pusher-js'),
    ]);
    return new EchoClient({
      broadcaster: 'reverb',
      key: env.key,
      wsHost: env.host,
      wsPort: env.port,
      wssPort: env.port,
      forceTLS: env.forceTLS,
      enabledTransports: ['ws', 'wss'],
      Pusher,
      channelAuthorization: {
        transport: 'ajax',
        endpoint: `${APP_BASE_URL}/broadcasting/auth`,
        customHandler: ({ socketId, channelName }, callback) => {
          api
            .post(
              '/broadcasting/auth',
              { socket_id: socketId, channel_name: channelName },
              {
                baseURL: APP_BASE_URL,
                withCredentials: true,
                // Cross-origin deployments need the header sent explicitly.
                withXSRFToken: true,
              }
            )
            .then(response => callback(null, response.data))
            .catch((error: unknown) =>
              callback(
                error instanceof Error ? error : new Error('auth failed'),
                null
              )
            );
        },
      },
    }) as RealtimeEcho;
  } catch {
    return null;
  }
}

/**
 * Resolves the shared Echo client, or null when websockets are not
 * configured (or failed to start): callers then rely on polling.
 */
export function loadEcho(): Promise<RealtimeEcho | null> {
  if (testOverride !== undefined) return Promise.resolve(testOverride);
  if (!isRealtimeConfigured()) return Promise.resolve(null);
  if (!instance) instance = createEcho();
  return instance;
}

/** Closes the socket (logout). The next `loadEcho` builds a fresh client. */
export function disconnectEcho(): void {
  const pending = instance;
  instance = null;
  void pending?.then(echo => echo?.disconnect());
}

/** Test seam: `undefined` restores the real behaviour. */
export function setEchoForTests(echo: RealtimeEcho | null | undefined): void {
  testOverride = echo;
}
